import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== action.payload.id)
        };
      }
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const [currentUserId, setCurrentUserId] = useState(null);

  // Load current user and their cart
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (currentUser?.id) {
      setCurrentUserId(currentUser.id);
      const userCart = localStorage.getItem(`jrweb_user_${currentUser.id}_cart`);
      if (userCart) {
        dispatch({ type: 'LOAD_CART', payload: JSON.parse(userCart) });
      }
    } else {
      // For non-logged-in users, use general cart
      const generalCart = localStorage.getItem('jrweb_cart');
      if (generalCart) {
        dispatch({ type: 'LOAD_CART', payload: JSON.parse(generalCart) });
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (currentUserId) {
      localStorage.setItem(`jrweb_user_${currentUserId}_cart`, JSON.stringify(state.items));
    } else {
      localStorage.setItem('jrweb_cart', JSON.stringify(state.items));
    }
  }, [state.items, currentUserId]);

  // Update user ID when user logs in/out
  useEffect(() => {
    const handleStorageChange = () => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (currentUser?.id !== currentUserId) {
        if (currentUser?.id) {
          setCurrentUserId(currentUser.id);
          const userCart = localStorage.getItem(`jrweb_user_${currentUser.id}_cart`);
          if (userCart) {
            dispatch({ type: 'LOAD_CART', payload: JSON.parse(userCart) });
          } else {
            dispatch({ type: 'CLEAR_CART' });
          }
        } else {
          setCurrentUserId(null);
          dispatch({ type: 'CLEAR_CART' });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUserId]);

  const addToCart = (product) => {
    // Create a clean product object with only relevant attributes based on category
    const cleanProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
      quantity: product.quantity || 1
    };

    // Add category-specific attributes
    if (product.category === 'Electronics') {
      // Electronics: Show color only if available
      if (product.selectedColor || product.colors?.[0]) {
        cleanProduct.selectedColor = product.selectedColor || product.colors[0];
      }
    } else if (product.category === 'Clothing') {
      // Clothing: Show both color and size if available
      if (product.selectedColor || product.colors?.[0]) {
        cleanProduct.selectedColor = product.selectedColor || product.colors[0];
      }
      if (product.selectedSize || product.sizes?.[0]) {
        cleanProduct.selectedSize = product.selectedSize || product.sizes[0];
      }
    } else if (product.category === 'Sports & Fitness') {
      // Sports: Show color and size if available
      if (product.selectedColor || product.colors?.[0]) {
        cleanProduct.selectedColor = product.selectedColor || product.colors[0];
      }
      if (product.selectedSize || product.sizes?.[0]) {
        cleanProduct.selectedSize = product.selectedSize || product.sizes[0];
      }
    } else if (product.category === 'Beauty & Personal Care') {
      // Beauty: Show color if available (for makeup items)
      if (product.selectedColor || product.colors?.[0]) {
        cleanProduct.selectedColor = product.selectedColor || product.colors[0];
      }
    }
    // Food & Beverages and Home & Garden: No color/size attributes added

    dispatch({ type: 'ADD_TO_CART', payload: cleanProduct });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider value={{
      items: state.items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemsCount,
      isInCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};